import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { motion } from 'framer-motion';
import { alaViewport } from './alaMotion';

const imageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Landmark360 resident experience',
    },
  },
};

const ctaDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/landmark360',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Learn More Now',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

export function HomeLandmark360() {
  const image = useSlotGroup('homelandmark360.image', imageDef);
  const cta = useSlotGroup('homelandmark360.cta', ctaDef);

  return (
    <section className="relative min-h-[75vh] overflow-hidden text-[rgb(var(--ala-on-primary))]" style={{ backgroundColor: 'rgb(var(--ala-dark))' }}>
      <EditableSlotGroup groupPath="homelandmark360.image" definition={imageDef}>
        <div className="absolute inset-0">
          <VibesitesImage
            url={
              image.image ||
              ''
            }
            alt={image.alt || ''}
            sizes="100vw"
            className="h-full w-full object-cover"
          />
          <div
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(90deg, rgb(var(--ala-dark)) 0%, rgb(var(--ala-dark)/0.4) 100%)',
            }}
          />
        </div>
      </EditableSlotGroup>

      <div className="relative z-10 mx-auto flex min-h-[75vh] max-w-[1440px] flex-col justify-center gap-6 px-4 py-24 md:px-8 md:py-32">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-[4px] bg-[rgb(var(--ala-accent))]" aria-hidden />
          <RichTextContent
            path="homelandmark360.eyebrow"
            label="Eyebrow"
            as="span"
            className="font-heading text-sm font-semibold uppercase tracking-[0.2em] text-[rgb(var(--ala-on-primary))]/80"
          />
        </div>
        <div className="flex max-w-3xl flex-wrap items-end gap-x-3 gap-y-1">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={alaViewport}
            transition={{ duration: 0.7 }}
          >
            <RichTextContent
              path="homelandmark360.headline_prefix"
              label="Headline Prefix"
              as="h2"
              className="font-heading text-[clamp(2.5rem,8vw,5.5rem)] font-bold uppercase leading-[0.95] tracking-[-0.04em]"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={alaViewport}
            transition={{ duration: 0.7, delay: 0.15 }}
          >
            <RichTextContent
              path="homelandmark360.headline_accent"
              label="Headline Accent"
              as="span"
              className="font-heading text-[clamp(2.5rem,8vw,5.5rem)] font-bold uppercase leading-[0.95] tracking-[-0.04em] text-[rgb(var(--ala-accent))]"
            />
          </motion.div>
        </div>
        <RichTextContent
          path="homelandmark360.body"
          label="Body"
          as="p"
          className="max-w-xl font-body text-base leading-relaxed text-[rgb(var(--ala-on-primary))]/85 md:text-lg"
        />
        <EditableSlotGroup groupPath="homelandmark360.cta" definition={ctaDef}>
          <a
            href={cta.url || '/landmark360'}
            target={cta._target || '_self'}
            className="mt-2 inline-flex w-fit items-center gap-2 rounded-full bg-[rgb(var(--ala-secondary))] px-6 py-3.5 font-heading text-sm uppercase tracking-wide text-[rgb(var(--ala-on-secondary))] transition hover:bg-[#3d7fd9]"
          >
            {cta.label || 'Learn More Now'}
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path d="M5 12h14M13 5l7 7-7 7" />
            </svg>
          </a>
        </EditableSlotGroup>

        <div className="pointer-events-none absolute right-6 top-1/2 hidden -translate-y-1/2 md:right-12 lg:block">
          <div className="relative flex h-[200px] w-[200px] items-center justify-center">
            <motion.div
              className="absolute inset-0 rounded-full border-2 border-dashed border-[rgb(var(--ala-accent))]"
              animate={{ rotate: 360 }}
              transition={{ duration: 18, repeat: Infinity, ease: 'linear' }}
            />
            <div className="relative flex h-[172px] w-[172px] flex-col items-center justify-center gap-1 rounded-full bg-[#4a3739]/80 px-4 text-center">
              <RichTextContent
                path="homelandmark360.badge_number"
                label="Badge Number"
                as="p"
                className="font-heading text-[60px] font-bold uppercase leading-none tracking-[-3.04px] text-[rgb(var(--ala-on-primary))]"
              />
              <RichTextContent
                path="homelandmark360.badge_label"
                label="Badge Label"
                as="p"
                className="font-heading text-[16px] font-semibold uppercase leading-none tracking-[0.18em] text-[rgb(var(--ala-on-primary))]"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'HomeLandmark360',
  displayName: 'Home Landmark360',
  tokens: {
    'homelandmark360.eyebrow': {
      label: 'Eyebrow',
      type: 'richtext',
      defaultValue: '<p>The Resident Experience</p>',
    },
    'homelandmark360.headline_prefix': {
      label: 'Headline Prefix',
      type: 'richtext',
      defaultValue: '<p>Landmark</p>',
    },
    'homelandmark360.headline_accent': {
      label: 'Headline Accent',
      type: 'richtext',
      defaultValue: '<p>360®</p>',
    },
    'homelandmark360.body': {
      label: 'Body',
      type: 'richtext',
      defaultValue:
        '<p>With service guarantees, responsive maintenance, and resident-first support, we make every step of your living experience easier, more convenient, and worry-free.</p>',
    },
    'homelandmark360.badge_number': {
      label: 'Badge Number',
      type: 'richtext',
      defaultValue: '<p>360</p>',
    },
    'homelandmark360.badge_label': {
      label: 'Badge Label',
      type: 'richtext',
      defaultValue: '<p>Support</p>',
    },
  },
  slotGroups: {
    'homelandmark360.image': imageDef,
    'homelandmark360.cta': ctaDef,
  },
};

(HomeLandmark360 as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default HomeLandmark360;
